/**
 * Shared utilities for Invoice integration forms (QuickBooks and Xero)
 */
import * as Yup from "yup";
import { Invoice2, LineItem2, InvoiceApprovalStatus, TransactionLine, ApprovalProgress } from "src/docuclipper/DocuclipperTypes";

/**
 * Default line item structure for new lines
 */
export const defaultInvoiceLine: Partial<LineItem2> = {
  item: "",
  quantity: "",
  unitPrice: "",
  price: "",
  categoryAutomated: "",
  serviceAutomated: "",
  categoryEdited: "",
  serviceEdited: "",
  taxAutomated: "",
  taxEdited: "",
};

/**
 * Props shared between QuickBooks and Xero invoice forms
 */
export type InvoiceFormProps = {
  invoice: Invoice2;
  getMarginTop: (
    index: number,
    lineItem: any,
    margin: string,
    defaultMargin: string
  ) => string;
  tl: TransactionLine;
};

/**
 * Create base validation schema for invoice forms
 * This includes common fields that are the same for both integrations
 */
export const createBaseValidationSchema = (docType: string) => {
  return {
    id: Yup.string().required("ID is required"),
    date: Yup.string()
      .trim()
      .matches(/\d{4}-\d{2}-\d{2}/, "Invalid date, use YYYY-MM-DD format")
      .required("Date is required"),
    dueDate: Yup.string()
      .trim()
      .matches(/\d{4}-\d{2}-\d{2}/, "Invalid date, use YYYY-MM-DD format"),
    total: Yup.number().required("Total is required"),
    tax: Yup.number().required("Tax is required"),
  };
};

/**
 * Create line items validation for QuickBooks
 */
export const createQboLineItemsValidation = (
  docType: string,
  billLineItemsMapTo: "categories" | "products_services" = "categories"
) => {
  return Yup.array().of(
    Yup.object()
      .shape({
        item: Yup.string().required("Item is required"),
        quantity: Yup.number().required("Quantity is required"),
        unitPrice: Yup.number().required("Unit price is required"),
        price: Yup.number().required("Price is required"),
        tax: Yup.string().nullable(),
        ...(["bill", "expense"].includes(docType) && {
          category: Yup.string().nullable(),
          categoryEdited: Yup.string().nullable(),
          categoryAutomated: Yup.string().nullable(),
        }),
        ...(["invoice"].includes(docType) && {
          service: Yup.string().nullable(),
          serviceAutomated: Yup.string().nullable(),
          serviceEdited: Yup.string().nullable(),
        }),
      })
      .test("at-least-one-category", "Category is required", function (value) {
        if (
          ["expense"].includes(docType) ||
          (["bill"].includes(docType) && billLineItemsMapTo === "categories")
        ) {
          return !!(value.category || value.categoryEdited || value.categoryAutomated);
        }
        return true;
      })
      .test("at-least-one-service", "Product/service is required", function (value) {
        if (
          ["invoice"].includes(docType) ||
          (["bill"].includes(docType) && billLineItemsMapTo === "products_services")
        ) {
          return !!(value.service || value.serviceEdited || value.serviceAutomated);
        }
        return true;
      })
  );
};

/**
 * Create line items validation for Xero
 * Note: Xero supports both items (serviceXero) and accounts (categoryXero) for both invoices and bills
 * Both fields are optional - no requirement that either must be present
 */
export const createXeroLineItemsValidation = (docType: string) => {
  return Yup.array().of(
    Yup.object()
      .shape({
        item: Yup.string().required("Item is required"),
        quantity: Yup.number().required("Quantity is required"),
        unitPrice: Yup.number().required("Unit price is required"),
        price: Yup.number().required("Price is required"),
        taxXero: Yup.string().nullable(),
        // Both invoices and bills can have items (serviceXero) - optional
        serviceXero: Yup.string().nullable(),
        serviceXeroAutomated: Yup.string().nullable(),
        serviceXeroEdited: Yup.string().nullable(),
        // Both invoices and bills can have accounts (categoryXero) - optional
        categoryXero: Yup.string().nullable(),
        categoryXeroEdited: Yup.string().nullable(),
        categoryXeroAutomated: Yup.string().nullable(),
      })
      // Both fields are optional - no validation requirement
  );
};

/**
 * Create customer validation for QuickBooks
 */
export const createQboCustomerValidation = () => {
  return Yup.string().test(
    "customer-required",
    "Customer is required",
    function (this: any, value?: any) {
      if (!this.parent) return false;
      return !!(
        this.parent.customerEdited ||
        this.parent.customerAutomated ||
        this.parent.customer
      );
    }
  );
};

/**
 * Create customer validation for Xero
 */
export const createXeroCustomerValidation = () => {
  return Yup.string().test(
    "customer-required",
    "Customer is required",
    function (this: any, value?: any) {
      if (!this.parent) return false;
      return !!(
        this.parent.customerXeroEdited ||
        this.parent.customerXeroAutomated ||
        this.parent.customerXero
      );
    }
  );
};

/**
 * Create vendor validation for QuickBooks
 */
export const createQboVendorValidation = () => {
  return Yup.string().test(
    "vendor-required",
    "Vendor is required",
    function (this: any, value?: any) {
      if (!this.parent) return false;
      return !!(
        this.parent.vendorEdited ||
        this.parent.vendorAutomated ||
        this.parent.vendor
      );
    }
  );
};

/**
 * Create vendor validation for Xero
 */
export const createXeroVendorValidation = () => {
  return Yup.string().test(
    "vendor-required",
    "Contact is required",
    function (this: any, value?: any) {
      if (!this.parent) return false;
      return !!(
        this.parent.vendorXeroEdited ||
        this.parent.vendorXeroAutomated ||
        this.parent.vendorXero
      );
    }
  );
};

/**
 * Create account validation for QuickBooks
 */
export const createQboAccountValidation = () => {
  return Yup.string().test(
    "account-required",
    "Account is required",
    function (this: any, value?: any) {
      if (!this.parent) return false;
      return !!(
        this.parent.accountEdited ||
        this.parent.accountAutomated ||
        this.parent.account
      );
    }
  );
};

/**
 * Create account validation for Xero
 */
export const createXeroAccountValidation = () => {
  return Yup.string().test(
    "account-required",
    "Account is required",
    function (this: any, value?: any) {
      if (!this.parent) return false;
      return !!(
        this.parent.accountXeroEdited ||
        this.parent.accountXeroAutomated ||
        this.parent.accountXero
      );
    }
  );
};

/**
 * Pulse animation styles for CTA button
 */
export const pulseAnimationStyles = `
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.7);
    }
    70% {
      box-shadow: 0 0 0 8px rgba(0, 123, 255, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
    }
  }
`;

/**
 * Helper to get label by ID from an array of options
 */
export const getOptionLabelById = (
  options: Array<{ value: any; name: string }>,
  id: any
): string => {
  const option = options.find((x) => x.value === id);
  return option ? option.name : "";
};

/**
 * Create new line item with tax for the "Add Tax as Line Item" feature
 */
export const createTaxLineItem = (taxValue: string): Partial<LineItem2> => {
  return {
    item: "Tax",
    description: "Tax",
    quantity: "1",
    unitPrice: taxValue,
    price: taxValue,
    category: "",
    categoryName: "",
    categoryEdited: "",
    categoryEditedName: "",
    categoryAutomated: "",
    categoryAutomatedName: "",
    categoryExcel: "",
    categoryExcelEdited: "",
    categoryExcelAutomated: "",
    service: "",
    serviceName: "",
    serviceEdited: "",
    serviceEditedName: "",
    serviceAutomated: "",
    serviceAutomatedName: "",
    serviceExcel: "",
    serviceExcelEdited: "",
    serviceExcelAutomated: "",
    tax: "",
    taxName: "",
    taxEdited: "",
    taxEditedName: "",
    taxAutomated: "",
    taxAutomatedName: "",
    extraFields: {},
  };
};

